import React, { useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergency password reset, gated by a secret in the URL query string that
// must match EMERGENCY_RESET_SECRET on the backend. This page exists so the
// founder (or an ops operator) can reset a password without MongoDB access
// or a server shell when the normal forgot-password flow is broken.
//
// Visit: /emergency-reset?secret=YOUR_RENDER_ENV_SECRET
//
// Remove the EMERGENCY_RESET_SECRET env var on Render after use to disable
// the endpoint.
const EmergencyResetPage = () => {
  const [params] = useSearchParams();
  const secret = params.get('secret') || '';
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const canSubmit = secret && email && password.length >= 10 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/emergency-reset`,
        { email: email.trim(), new_password: password },
        { headers: { 'X-Emergency-Secret': secret } },
      );
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Could not reset password.';
      if (status === 404 && /not found/i.test(String(detail))) {
        setError('Emergency reset is disabled on the server. Set EMERGENCY_RESET_SECRET in Render env vars.');
      } else if (status === 403) {
        setError('Wrong secret. Check the value in your URL matches EMERGENCY_RESET_SECRET on Render.');
      } else {
        setError(String(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    if (!email) { setError('Enter the email of the account to delete.'); return; }
    if (!window.confirm(
      `This permanently deletes the user account for ${email}. You'll need to sign up fresh afterwards. Continue?`
    )) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/emergency-delete-user`,
        { email: email.trim() },
        { headers: { 'X-Emergency-Secret': secret } },
      );
      setDeleted(true);
      console.info('Emergency delete result', data);
      setTimeout(() => navigate('/signup'), 2500);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Could not delete user.';
      if (status === 404 && /not found/i.test(String(detail))) {
        setError('Emergency endpoints are disabled on the server. Set EMERGENCY_RESET_SECRET in Render env vars.');
      } else if (status === 403) {
        setError('Wrong secret. Check the value in your URL matches EMERGENCY_RESET_SECRET on Render.');
      } else if (status === 404) {
        setError(`No user found with email ${email}.`);
      } else {
        setError(String(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 mb-3">
          Emergency use
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1F3A68] mb-2">Emergency password reset</h1>
        <p className="text-sm text-slate-600 mb-6">
          Bypasses the email flow. Requires the operator secret as a URL
          parameter. Disable by removing EMERGENCY_RESET_SECRET on Render
          after use.
        </p>

        {!secret && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 mb-4">
            No secret in URL. Append <code>?secret=YOUR_VALUE</code> to the address bar where YOUR_VALUE is the EMERGENCY_RESET_SECRET you set in Render env vars.
          </div>
        )}

        {done && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 mb-4">
            Password reset. Redirecting to sign in.
          </div>
        )}

        {deleted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 mb-4">
            User deleted. Redirecting to sign up so you can create a fresh account.
          </div>
        )}

        {!done && !deleted && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New password (min 10 chars)</label>
              <input
                type="password"
                required
                minLength={10}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full h-12 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
              />
              {confirm && password !== confirm && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>
              )}
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full h-12 bg-[#1F3A68] text-white rounded-lg font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}

        {!done && !deleted && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-2">Danger zone</p>
            <p className="text-sm text-slate-600 mb-3">
              If resetting the password isn't working, delete the user account entirely and sign up fresh. This permanently removes the user matching the email above. Only the email field is needed (the password fields are ignored).
            </p>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!secret || !email || submitting}
              className="w-full h-12 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Deleting…' : 'Delete user account'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyResetPage;
